import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  useCart,
  itemKey,
  entryMatchesItem,
  isPackItem,
  isProductItem,
  packItemSizes,
  type PackCartItem,
} from '@/features/cart/CartContext';
import { usePackIncludes } from '@/features/cart/CartPackLine';
import { PACK_PATH, packName } from '@/lib/catalog/packs';
import { useMeta } from '@/client/lib/useMeta';
import { api, ApiClientError, sizeRequiredIds, type QuoteIssue, type StockEntry } from '@/client/lib/api';
import { getCartId } from '@/client/lib/cartId';
import { isEU } from '@/config/site';
import { INTL_LOCALE } from '@/lib/seo';
import type { CartRequestLine, Locale, OrderTotals, SuggestedAddress } from '@/types';
import { track } from '@/client/lib/analytics';
import { formatMoney } from '@/lib/money';
import { lineKey } from '@/lib/cart';
import { findZone, formatDeliveryEstimate } from '@/lib/shipping';
import { EmptyState } from '@/components/States';
import { SectionHeader } from '@/components/SectionHeader';
import { Turnstile, TURNSTILE_ENABLED } from '@/components/Turnstile';
import { useI18n } from '@/client/i18n/I18nContext';
import { lp } from '@/client/i18n/locale';

// Shipping destinations: ISO 3166-1 alpha-2 code (what the API receives) +
// Spanish fallback label. The select shows Intl.DisplayNames in the active
// locale (see countryOptions); the fallback only covers a runtime without it.
const COUNTRIES: [code: string, fallbackLabel: string][] = [
  // España
  ['ES', 'España'],
  // Europa continental
  ['PT', 'Portugal'],
  ['FR', 'Francia'],
  ['DE', 'Alemania'],
  ['IT', 'Italia'],
  ['NL', 'Países Bajos'],
  ['BE', 'Bélgica'],
  ['LU', 'Luxemburgo'],
  ['AT', 'Austria'],
  ['IE', 'Irlanda'],
  ['CH', 'Suiza'],
  ['NO', 'Noruega'],
  ['SE', 'Suecia'],
  ['DK', 'Dinamarca'],
  ['FI', 'Finlandia'],
  ['PL', 'Polonia'],
  ['GR', 'Grecia'],
  // Internacional
  ['GB', 'Reino Unido'],
  ['US', 'Estados Unidos'],
  ['CA', 'Canadá'],
  ['MX', 'México'],
  ['AR', 'Argentina'],
  ['JP', 'Japón'],
  ['AU', 'Australia'],
];

/**
 * API error code → dictionary key. The ES values equal the Worker's messages
 * (routes/checkout.ts), so the Spanish site reads exactly what it did before.
 */
const CHECKOUT_ERROR_KEYS: Record<string, string> = {
  invalid_request: 'checkout.error.invalidRequest',
  cart_invalid: 'checkout.error.cartInvalid',
  turnstile_failed: 'checkout.error.turnstile',
};

/** The default destination (first in COUNTRIES). */
const DEFAULT_COUNTRY = 'ES';

interface CountryOption {
  code: string;
  label: string;
}

/**
 * Country options with names in `locale` (Intl.DisplayNames, falling back to
 * the Spanish label), in the curated COUNTRIES order (Spain, Europe by market
 * relevance, then international), which is the same in every language.
 */
function countryOptions(locale: Locale): CountryOption[] {
  const tag = INTL_LOCALE[locale];
  let names: Intl.DisplayNames | undefined;
  try {
    names = new Intl.DisplayNames([tag], { type: 'region' });
  } catch {
    names = undefined;
  }
  return COUNTRIES.map(([code, fallback]) => {
    const name = names?.of(code);
    return { code, label: name && name !== code ? name : fallback };
  });
}

/**
 * Pack ids with a blocking issue in an issues payload (any shape): any issue
 * carrying `packId` except the informational `backorder` (a component's
 * size_required included: the pack page is where its sizes are chosen).
 */
function packIssueIds(issues: unknown): string[] {
  if (!Array.isArray(issues)) return [];
  const ids = issues.flatMap((i: unknown) => {
    const it = i as Partial<QuoteIssue> | null;
    return it && typeof it.packId === 'string' && it.packId && it.code !== 'backorder' ? [it.packId] : [];
  });
  return [...new Set(ids)];
}

export function CheckoutPage() {
  const { t, locale } = useI18n();
  useMeta({ title: t('meta.checkout.title'), path: '/checkout', noindex: true });
  const countries = useMemo(() => countryOptions(locale), [locale]);
  const countryLabel = (code: string) => countries.find((c) => c.code === code)?.label ?? code;
  const { items, subtotalCents, currency, clear, remove } = useCart();
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [manualRef, setManualRef] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<SuggestedAddress | null>(null);
  const [addressNotFound, setAddressNotFound] = useState(false);
  // Transmit-step availability outcomes (server checks stock right before payment).
  const [soldOut, setSoldOut] = useState<StockEntry[] | null>(null);
  const [backorder, setBackorder] = useState<StockEntry[] | null>(null);
  // Products the server says need a size (`size_required`): a line stored
  // before the product got sizes. Fed by the quote and by a cart_invalid error.
  const [needsSize, setNeedsSize] = useState<string[]>([]);
  // Packs with a blocking issue (a component sold out / not purchasable / a
  // size missing, or the pack itself gone): fed by the quote and by a
  // cart_invalid error. The buyer reviews the pack or removes its line.
  const [packIssues, setPackIssues] = useState<string[]>([]);
  const includes = usePackIncludes();
  // soldOut / backorder entry → its key (pack, product, or product + size) and label.
  const entryKey = (e: StockEntry) => `${e.packId ?? ''}|${lineKey(e.productId, e.size)}`;
  const entryLabel = (e: StockEntry) => {
    const product = e.size ? `${e.name} · ${t('cart.size')} ${e.size}` : e.name;
    if (!e.packId) return product;
    // The pack line's name in the active language (the cart has it), else the API's.
    const line = items.find((i): i is PackCartItem => isPackItem(i) && i.pack.id === e.packId);
    const pack = line ? packName(line.pack, locale) : (e.packName ?? '');
    return pack ? `${pack}: ${product}` : product;
  };
  // Request lines: `size` only for sized lines (unsized payloads unchanged); a
  // pack line sends its id, quantity and chosen sizes (the server expands it).
  const requestItems = (): CartRequestLine[] =>
    items.map((i) => {
      if (isPackItem(i)) {
        const sizes = packItemSizes(i.pack);
        return {
          packId: i.pack.id,
          quantity: i.quantity,
          ...(Object.keys(sizes).length > 0 ? { sizes } : {}),
        };
      }
      return {
        productId: i.product.id,
        quantity: i.quantity,
        ...(i.product.size ? { size: i.product.size } : {}),
      };
    });
  const [addrCheck, setAddrCheck] = useState<'idle' | 'checking' | 'verified' | 'none' | 'error'>('idle');
  const [addrOptions, setAddrOptions] = useState<SuggestedAddress[]>([]);
  const [quote, setQuote] = useState<{ totals: OrderTotals; shippingKnown: boolean; issues?: QuoteIssue[] } | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  // Two-step checkout: fill the form, then review a summary before paying.
  const [stage, setStage] = useState<'form' | 'review'>('form');
  const [tsToken, setTsToken] = useState('');
  const [tsReset, setTsReset] = useState(0);
  // Explicit acceptance of the purchase terms + privacy policy (gates payment).
  const [accepted, setAccepted] = useState(false);
  const [form, setForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    shippingAddress1: '',
    shippingAddress2: '',
    shippingCity: '',
    shippingPostalCode: '',
    shippingProvince: '',
    shippingCountry: DEFAULT_COUNTRY,
  });

  // Authoritative price breakdown (subtotal, VAT, shipping, total) from the
  // server whenever the cart or destination country changes.
  const itemsKey = items.map((i) => `${itemKey(i)}:${i.quantity}`).join(',');
  useEffect(() => {
    if (items.length === 0) {
      setQuote(null);
      return;
    }
    let alive = true;
    setQuoteLoading(true);
    api
      .quote({
        items: requestItems(),
        country: form.shippingCountry,
      })
      .then((q) => {
        if (!alive) return;
        setQuote(q);
        setNeedsSize(sizeRequiredIds(q.issues));
        setPackIssues(packIssueIds(q.issues));
      })
      .catch(() => alive && setQuote(null))
      .finally(() => alive && setQuoteLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey, form.shippingCountry]);

  if (manualRef) {
    return (
      <div className="container">
        <div className="state">
          <p className="eyebrow">CARO</p>
          <h1 className="section-title">{t('checkout.manualTitle')}</h1>
          <p style={{ maxWidth: '60ch', marginInline: 'auto' }}>
            {t('checkout.manualBody')} <strong>{manualRef}</strong>
          </p>
          <div className="mt-6">
            <Link className="btn" to={lp('/')}>{t('common.backHome')}</Link>
          </div>
        </div>
      </div>
    );
  }

  // Sold out at the transmit step (non pre-order). Terminal apology screen with
  // an optional way to get in touch + a way back to the shop. Cart is untouched.
  if (soldOut) {
    return (
      <div className="container">
        <div className="state">
          <p className="eyebrow">CARO</p>
          <h1 className="section-title">{t('checkout.soldOutTitle')}</h1>
          <p style={{ maxWidth: '60ch', marginInline: 'auto' }}>{t('checkout.soldOutBody')}</p>
          <ul style={{ listStyle: 'none', padding: 0, margin: 'var(--space-4) 0' }}>
            {soldOut.map((i) => (
              <li key={entryKey(i)}><strong>{entryLabel(i)}</strong></li>
            ))}
          </ul>
          <p className="text-muted" style={{ maxWidth: '50ch', marginInline: 'auto' }}>
            <Link className="link-underline" to={lp('/contacto')}>{t('checkout.soldOutContact')}</Link>
          </p>
          <div className="mt-6">
            <Link className="btn" to={lp('/shop/beauty')}>{t('checkout.soldOutBack')}</Link>
          </div>
        </div>
      </div>
    );
  }

  // Short pre-order (reasortimiento) lines: warn about the delay and let the
  // buyer continue (accept the back-order) or remove the affected pieces.
  if (backorder) {
    return (
      <div className="container">
        {TURNSTILE_ENABLED ? (
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <Turnstile onVerify={setTsToken} resetSignal={tsReset} />
          </div>
        ) : null}
        <div className="caro-note" style={{ maxWidth: '60ch', margin: 'var(--space-6) auto', borderColor: 'var(--color-brown)' }}>
          <p className="caro-note__label">{t('checkout.backorderTitle')}</p>
          <p className="text-muted">{t('checkout.backorderBody')}</p>
          <ul style={{ margin: 'var(--space-3) 0', paddingLeft: 0, listStyle: 'none' }}>
            {backorder.map((i) => (
              <li
                key={entryKey(i)}
                style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)', alignItems: 'center', paddingBlock: 'var(--space-1)' }}
              >
                <strong>{entryLabel(i)}</strong>
                <button
                  type="button"
                  className="btn btn--ghost"
                  style={{ padding: '4px 12px', minHeight: 'auto', whiteSpace: 'nowrap' }}
                  onClick={() => {
                    // The flagged line (product + size), every line of the product,
                    // or the pack line (a pack is removed whole, so all its entries go).
                    items
                      .filter((line) => entryMatchesItem(i, line))
                      .forEach((line) => remove(itemKey(line)));
                    const next = backorder.filter(
                      (x) => entryKey(x) !== entryKey(i) && !(i.packId && x.packId === i.packId),
                    );
                    if (next.length === 0) {
                      setBackorder(null);
                      setStage('review');
                    } else {
                      setBackorder(next);
                    }
                  }}
                >
                  {t('checkout.backorderRemove')}
                </button>
              </li>
            ))}
          </ul>
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginTop: 'var(--space-4)' }}>
            <button
              type="button"
              className="btn"
              disabled={status === 'loading' || (TURNSTILE_ENABLED && !tsToken)}
              onClick={() => {
                setBackorder(null);
                submit({ acceptBackorder: true });
              }}
            >
              {t('checkout.backorderContinue')}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setBackorder(null);
                setStage('review');
              }}
            >
              {t('checkout.editOrder')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="container">
        <EmptyState
          title={t('checkout.emptyTitle')}
          message={t('checkout.emptyMsg')}
          action={<Link className="btn" to={lp('/shop/beauty')}>{t('common.viewBeauty')}</Link>}
        />
      </div>
    );
  }

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  // Editing an address field invalidates a previous validation result.
  const setAddr = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setAddrCheck('idle');
    setAddrOptions([]);
  };

  async function submit(opts?: {
    acceptUnverified?: boolean;
    acceptBackorder?: boolean;
    customer?: typeof form;
  }) {
    const f = opts?.customer ?? form;
    if (TURNSTILE_ENABLED && !tsToken) {
      setError(t('checkout.genericError'));
      setStatus('error');
      return;
    }
    setStatus('loading');
    setError(null);
    track('begin_checkout', { itemCount: items.length });
    try {
      const res = await api.checkout({
        items: requestItems(),
        customer: { ...f, billingCountry: f.shippingCountry },
        // The buyer's real language (ES at the root, EN/FR under /en, /fr), stored on the
        // order so after-sales can address them in it.
        locale,
        acceptUnverified: opts?.acceptUnverified ?? false,
        acceptBackorder: opts?.acceptBackorder ?? false,
        cartId: getCartId(),
        turnstileToken: tsToken,
      });
      // Transmit-step availability (checked server-side right before payment).
      // Sold out (non pre-order): a piece just sold — show the apology screen.
      if (res.soldOut) {
        setSoldOut(res.soldOut);
        setStatus('idle');
        setTsReset((n) => n + 1);
        window.scrollTo({ top: 0 });
        return;
      }
      // Short pre-order lines: warn about the delay, let them continue or remove.
      if (res.backorder) {
        setBackorder(res.backorder);
        setStatus('idle');
        setTsReset((n) => n + 1);
        window.scrollTo({ top: 0 });
        return;
      }
      // Address needs review: offer the corrected suggestion. Drop back to the
      // form so the note and the editable fields are visible together.
      if (res.addressSuggestion) {
        setSuggestion(res.addressSuggestion);
        setAddressNotFound(false);
        setStage('form');
        setStatus('idle');
        setTsReset((n) => n + 1); // the token was consumed; get a fresh one for the retry
        return;
      }
      if (res.addressUnverified) {
        setAddressNotFound(true);
        setSuggestion(null);
        setStage('form');
        setStatus('idle');
        setTsReset((n) => n + 1); // the token was consumed; get a fresh one for the retry
        return;
      }
      // Address couldn't be verified and the customer accepted → manual by email.
      if (res.manualEnquiry) {
        clear();
        setManualRef(res.reference ?? '—');
        setStatus('idle');
        return;
      }
      // Persist order number so the return page can verify after redirect.
      if (res.orderNumber) sessionStorage.setItem('caro.lastOrder', res.orderNumber);
      if (res.mode === 'hosted' && res.hostedUrl) {
        // The cart is deliberately NOT cleared here: the sale isn't real until the
        // payment is verified. If the buyer abandons SumUp's checkout (or the card
        // fails) they come back to a cart that still holds their pieces —
        // CheckoutReturnPage clears it once the server confirms `paid`.
        window.location.href = res.hostedUrl;
        return;
      }
      // Widget mode: the SumUp card widget mounts using res.checkoutId.
      // See docs/sumup.md — falls back to hosted when the widget is not yet
      // activated on the merchant account.
      setError(t('checkout.widgetNote'));
      setStatus('error');
    } catch (err) {
      // The API's error text is Spanish: translate the known codes; any other
      // (e.g. a shipping reason) is still shown as sent, since it says why.
      // A sizeless line for a now-sized product: show the "choose a size" block
      // (with the product link) instead of the generic cart error.
      const details =
        err instanceof ApiClientError && err.code === 'cart_invalid'
          ? (err.details as { issues?: unknown } | undefined)?.issues
          : undefined;
      const missing = sizeRequiredIds(details);
      // A pack line the server can't sell as it is: the pack block (review / remove).
      const badPacks = packIssueIds(details);
      if (missing.length > 0 || badPacks.length > 0) {
        setNeedsSize(missing);
        setPackIssues(badPacks);
        setStage('form');
        setStatus('idle');
        setTsReset((n) => n + 1);
        window.scrollTo({ top: 0 });
        return;
      }
      const key = err instanceof ApiClientError ? CHECKOUT_ERROR_KEYS[err.code] : undefined;
      const msg = key
        ? t(key)
        : err instanceof ApiClientError
          ? err.message
          : t('checkout.genericError');
      setError(msg);
      setStatus('error');
      setTsReset((n) => n + 1);
    }
  }

  // Step 1 → 2: validate the address (once) and show the review summary. The
  // server re-validates everything again at "Confirm and pay" (submit()).
  async function onContinue(e: React.FormEvent) {
    e.preventDefault();
    setSuggestion(null);
    setAddressNotFound(false);
    setError(null);
    // A line still needs a size: the block at the top says which; don't advance.
    if (sizeless.length > 0 || packProblems.length > 0) {
      window.scrollTo({ top: 0 });
      return;
    }
    const addressComplete = !!(form.shippingAddress1 && form.shippingCity && form.shippingPostalCode);
    if (addressComplete && addrCheck !== 'verified') {
      setAddrCheck('checking');
      setAddrOptions([]);
      try {
        const { suggestions } = await api.validateAddress({
          address1: form.shippingAddress1,
          city: form.shippingCity,
          postalCode: form.shippingPostalCode,
          country: form.shippingCountry,
        });
        if (suggestions.length === 1) {
          applyAddress(suggestions[0]!);
          setAddrCheck('verified');
        } else if (suggestions.length > 1) {
          // Ambiguous: let the buyer pick one, then press Continue again.
          setAddrOptions(suggestions);
          setAddrCheck('idle');
          return;
        } else {
          // Couldn't verify — still allow review; checkout() is the authority.
          setAddrCheck('none');
        }
      } catch {
        setAddrCheck('error'); // validator down → fail-open, proceed.
      }
    }
    setStage('review');
    window.scrollTo({ top: 0 });
  }

  // Apply a suggestion's geocoded fields; the complemento (address2) is kept.
  function applyAddress(s: SuggestedAddress): typeof form {
    const f = {
      ...form,
      shippingAddress1: s.address1 || form.shippingAddress1,
      shippingCity: s.city || form.shippingCity,
      shippingPostalCode: s.postalCode || form.shippingPostalCode,
      shippingProvince: s.province || form.shippingProvince,
      shippingCountry: s.country || form.shippingCountry,
    };
    setForm(f);
    return f;
  }

  function applySuggestion() {
    if (!suggestion) return;
    const f = applyAddress(suggestion);
    setSuggestion(null);
    submit({ customer: f });
  }

  // Explicit "Validate address" button (next to the address fields).
  async function validateNow() {
    if (!form.shippingAddress1 || !form.shippingCity || !form.shippingPostalCode) return;
    setAddrCheck('checking');
    setAddrOptions([]);
    setSuggestion(null);
    setAddressNotFound(false);
    try {
      const { suggestions } = await api.validateAddress({
        address1: form.shippingAddress1,
        city: form.shippingCity,
        postalCode: form.shippingPostalCode,
        country: form.shippingCountry,
      });
      if (suggestions.length === 0) {
        setAddrCheck('none');
      } else if (suggestions.length === 1) {
        applyAddress(suggestions[0]!);
        setAddrCheck('verified');
      } else {
        setAddrOptions(suggestions);
        setAddrCheck('idle');
      }
    } catch {
      setAddrCheck('error');
    }
  }

  function chooseOption(s: SuggestedAddress) {
    applyAddress(s);
    setAddrOptions([]);
    setAddrCheck('verified');
  }

  const hasAddress = !!(form.shippingAddress1 && form.shippingCity && form.shippingPostalCode);
  // Estimated delivery for the chosen country's zone (SHIPPING_TIMES: preparation
  // + transit), shown with a priced shipping line. Short pre-order lines
  // (`backorder`) are delayed: their notice (checkout.backorderTitle) prevails.
  const hasBackorder = !!quote?.issues?.some((i) => i.code === 'backorder');
  const deliveryEstimate =
    hasAddress && !quoteLoading && quote?.shippingKnown && !hasBackorder
      ? formatDeliveryEstimate(findZone(form.shippingCountry)?.id ?? '', t)
      : '';
  // Sizeless lines of products that now require a size (blocking until fixed).
  const sizeless = items
    .filter(isProductItem)
    .filter((i) => !i.product.size && needsSize.includes(i.product.id));
  // Pack lines the server could not sell as they are (blocking until fixed).
  const packProblems = items.filter(
    (i): i is PackCartItem => isPackItem(i) && packIssues.includes(i.pack.id),
  );

  return (
    <div className="container">
      <SectionHeader as="h1" eyebrow={t('checkout.eyebrow')} title={t('checkout.title')} />

      {/* Anti-bot: one widget mounted for the whole checkout so a fresh token is
          available on every submit path (review confirm, apply-suggestion,
          send-manual). Renders nothing unless the site key is configured. */}
      {TURNSTILE_ENABLED ? (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Turnstile onVerify={setTsToken} resetSignal={tsReset} />
        </div>
      ) : null}

      {sizeless.length > 0 ? (
        <div
          className="caro-note"
          role="alert"
          style={{ maxWidth: '60ch', marginBottom: 'var(--space-5)', borderColor: 'var(--color-brown)' }}
        >
          <p className="caro-note__label">{t('checkout.sizeRequiredTitle')}</p>
          <p className="text-muted">{t('checkout.sizeRequiredBody')}</p>
          <ul style={{ margin: 'var(--space-3) 0', paddingLeft: 0, listStyle: 'none' }}>
            {sizeless.map((line) => (
              <li
                key={itemKey(line)}
                style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap', paddingBlock: 'var(--space-1)' }}
              >
                <span>
                  {t('checkout.sizeRequiredFor')} <strong>{line.product.name}</strong>
                </span>
                <span style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <Link
                    className="btn"
                    style={{ padding: '4px 12px', minHeight: 'auto', whiteSpace: 'nowrap' }}
                    to={lp(`/shop/${line.product.category}/${line.product.slug}`)}
                    onClick={() => remove(itemKey(line))}
                  >
                    {t('checkout.sizeRequiredChoose')}
                  </Link>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    style={{ padding: '4px 12px', minHeight: 'auto', whiteSpace: 'nowrap' }}
                    onClick={() => remove(itemKey(line))}
                  >
                    {t('cart.remove')}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {packProblems.length > 0 ? (
        <div
          className="caro-note"
          role="alert"
          style={{ maxWidth: '60ch', marginBottom: 'var(--space-5)', borderColor: 'var(--color-brown)' }}
        >
          <p className="caro-note__label">{t('checkout.packIssueTitle')}</p>
          <p className="text-muted">{t('checkout.packIssueBody')}</p>
          <ul style={{ margin: 'var(--space-3) 0', paddingLeft: 0, listStyle: 'none' }}>
            {packProblems.map((line) => (
              <li
                key={itemKey(line)}
                style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap', paddingBlock: 'var(--space-1)' }}
              >
                <strong>{packName(line.pack, locale)}</strong>
                <span style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <Link
                    className="btn"
                    style={{ padding: '4px 12px', minHeight: 'auto', whiteSpace: 'nowrap' }}
                    to={lp(`${PACK_PATH}/${line.pack.slug}`)}
                    onClick={() => remove(itemKey(line))}
                  >
                    {t('checkout.packIssueView')}
                  </Link>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    style={{ padding: '4px 12px', minHeight: 'auto', whiteSpace: 'nowrap' }}
                    onClick={() => remove(itemKey(line))}
                  >
                    {t('cart.remove')}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {suggestion ? (
        <div className="caro-note" style={{ marginBottom: 'var(--space-5)' }}>
          <p className="caro-note__label">{t('checkout.addrSuggestTitle')}</p>
          <p className="text-muted">{t('checkout.addrSuggestBody')}</p>
          <p style={{ marginTop: 'var(--space-2)' }}>{suggestion.label}</p>
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginTop: 'var(--space-3)' }}>
            <button
              type="button"
              className="btn"
              disabled={TURNSTILE_ENABLED && !tsToken}
              onClick={applySuggestion}
            >
              {t('checkout.addrUse')}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              disabled={TURNSTILE_ENABLED && !tsToken}
              onClick={() => submit({ acceptUnverified: true })}
            >
              {t('checkout.addrKeep')}
            </button>
          </div>
        </div>
      ) : null}

      {addressNotFound ? (
        <div className="caro-note" style={{ marginBottom: 'var(--space-5)', borderColor: 'var(--color-danger)' }}>
          <p className="caro-note__label" style={{ color: 'var(--color-danger)' }}>
            {t('checkout.addrNotFoundTitle')}
          </p>
          <p className="text-muted">{t('checkout.addrNotFoundBody')}</p>
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginTop: 'var(--space-3)' }}>
            <button type="button" className="btn btn--ghost" onClick={() => setAddressNotFound(false)}>
              {t('checkout.addrRetry')}
            </button>
            <button
              type="button"
              className="btn"
              disabled={TURNSTILE_ENABLED && !tsToken}
              onClick={() => submit({ acceptUnverified: true })}
            >
              {t('checkout.addrSendManual')}
            </button>
          </div>
        </div>
      ) : null}
      <div className="pdp" style={{ alignItems: 'start', paddingBottom: 'var(--space-8)' }}>
        {stage === 'form' ? (
        <form className="stack" onSubmit={onContinue}>
          <p className="eyebrow">{t('checkout.contact')}</p>
          <div className="field">
            <label htmlFor="co-email">{t('checkout.email')}</label>
            <input
              id="co-email"
              type="email"
              required
              autoComplete="email"
              value={form.email}
              onChange={set('email')}
            />
            <span className="text-muted" style={{ fontSize: 'var(--fs-xs)' }}>
              {t('checkout.emailHint')}
            </span>
          </div>
          <div className="field">
            <label htmlFor="co-phone">{t('checkout.phone')}</label>
            <input
              id="co-phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={form.phone}
              onChange={set('phone')}
            />
          </div>
          <div className="form-grid form-grid--2">
            <div className="field">
              <label htmlFor="co-fn">{t('checkout.firstName')}</label>
              <input id="co-fn" required value={form.firstName} onChange={set('firstName')} />
            </div>
            <div className="field">
              <label htmlFor="co-ln">{t('checkout.lastName')}</label>
              <input id="co-ln" required value={form.lastName} onChange={set('lastName')} />
            </div>
          </div>

          <p className="eyebrow" style={{ marginTop: 'var(--space-4)' }}>{t('checkout.shipping')}</p>
          <div className="field">
            <label htmlFor="co-addr">{t('checkout.address')}</label>
            <input id="co-addr" required value={form.shippingAddress1} onChange={setAddr('shippingAddress1')} />
          </div>
          <div className="field">
            <label htmlFor="co-addr2">{t('checkout.address2')}</label>
            <input id="co-addr2" value={form.shippingAddress2} onChange={set('shippingAddress2')} />
          </div>
          <div className="form-grid form-grid--2">
            <div className="field">
              <label htmlFor="co-city">{t('checkout.city')}</label>
              <input id="co-city" required value={form.shippingCity} onChange={setAddr('shippingCity')} />
            </div>
            <div className="field">
              <label htmlFor="co-zip">{t('checkout.zip')}</label>
              <input
                id="co-zip"
                required
                value={form.shippingPostalCode}
                onChange={setAddr('shippingPostalCode')}
                onBlur={() => {
                  // Auto-validate on leaving the postal code (only if not done yet).
                  if (addrCheck === 'idle') validateNow();
                }}
              />
            </div>
          </div>

          {/* Explicit address validation (does not use the complemento). */}
          <div
            className="addr-check"
            data-state={addrCheck}
          >
            <div className="addr-check__row">
              <div>
                <p className="addr-check__help">{t('checkout.addrHelp')}</p>
                {addrCheck === 'verified' ? (
                  <p className="addr-check__msg addr-check__msg--ok">{t('checkout.addrVerified')}</p>
                ) : null}
                {addrCheck === 'none' ? (
                  <p className="addr-check__msg addr-check__msg--err">{t('checkout.addrNone')}</p>
                ) : null}
                {addrCheck === 'error' ? (
                  <p className="addr-check__msg text-muted">{t('checkout.addrCheckError')}</p>
                ) : null}
              </div>
              <button
                type="button"
                className={addrCheck === 'verified' ? 'btn btn--ghost' : 'btn'}
                style={{ minHeight: 44, whiteSpace: 'nowrap' }}
                disabled={addrCheck === 'checking'}
                onClick={validateNow}
              >
                {addrCheck === 'checking'
                  ? t('checkout.addrChecking')
                  : addrCheck === 'verified'
                    ? t('checkout.addrVerified')
                    : t('checkout.validate')}
              </button>
            </div>

            {addrOptions.length > 0 ? (
              <div className="stack" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
                <p className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('checkout.addrChoose')}</p>
                {addrOptions.map((s, i) => (
                  <button
                    key={`${s.label}-${i}`}
                    type="button"
                    className="addr-check__option"
                    onClick={() => chooseOption(s)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <div className="form-grid form-grid--2">
            <div className="field">
              <label htmlFor="co-prov">{t('checkout.province')}</label>
              <input id="co-prov" value={form.shippingProvince} onChange={setAddr('shippingProvince')} />
            </div>
            <div className="field">
              <label htmlFor="co-country">{t('checkout.country')}</label>
              <select id="co-country" value={form.shippingCountry} onChange={setAddr('shippingCountry')}>
                {countries.map(({ code, label }) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {error ? <p style={{ color: 'var(--color-danger)' }}>{error}</p> : null}

          <button
            type="submit"
            className="btn btn--full"
            disabled={status === 'loading' || addrCheck === 'checking'}
          >
            {addrCheck === 'checking' ? t('checkout.addrChecking') : t('checkout.continue')}
          </button>
          <p className="text-muted" style={{ fontSize: 'var(--fs-xs)' }}>
            {t('checkout.secureNote')}
          </p>
        </form>
        ) : (
          <div className="stack">
            <p className="eyebrow">{t('checkout.reviewEyebrow')}</p>
            <h2 className="section-title" style={{ marginTop: 0 }}>{t('checkout.reviewTitle')}</h2>
            <p className="text-muted">{t('checkout.reviewIntro')}</p>

            <div className="stack" style={{ gap: 'var(--space-1)', paddingTop: 'var(--space-3)' }}>
              <p className="eyebrow">{t('checkout.reviewContact')}</p>
              <p>{form.firstName} {form.lastName}</p>
              <p className="text-muted">{form.email}</p>
              <p className="text-muted">{form.phone || t('checkout.reviewNoPhone')}</p>
            </div>

            <hr className="divider" style={{ margin: 'var(--space-3) 0' }} />

            <div className="stack" style={{ gap: 'var(--space-1)' }}>
              <p className="eyebrow">{t('checkout.reviewShipTo')}</p>
              <p>{form.shippingAddress1}</p>
              {form.shippingAddress2 ? <p className="text-muted">{form.shippingAddress2}</p> : null}
              <p className="text-muted">
                {form.shippingPostalCode} {form.shippingCity}
                {form.shippingProvince ? `, ${form.shippingProvince}` : ''}
              </p>
              <p className="text-muted">{countryLabel(form.shippingCountry)}</p>
            </div>

            {error ? <p style={{ color: 'var(--color-danger)' }}>{error}</p> : null}

            <label
              style={{
                display: 'flex',
                gap: 'var(--space-2)',
                alignItems: 'flex-start',
                marginTop: 'var(--space-4)',
                fontSize: 'var(--fs-sm)',
                color: 'var(--color-text-muted)',
              }}
            >
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                aria-label={`${t('checkout.acceptPre')} ${t('checkout.acceptTermsLink')} ${t('checkout.acceptMid')} ${t('checkout.acceptPrivacyLink')}`}
                style={{ marginTop: 3 }}
              />
              <span>
                {t('checkout.acceptPre')}{' '}
                <Link to={lp('/legal/condiciones-compra')} className="link-underline" target="_blank" rel="noopener noreferrer">
                  {t('checkout.acceptTermsLink')}
                </Link>{' '}
                {t('checkout.acceptMid')}{' '}
                <Link to={lp('/legal/privacidad')} className="link-underline" target="_blank" rel="noopener noreferrer">
                  {t('checkout.acceptPrivacyLink')}
                </Link>.
              </span>
            </label>

            <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginTop: 'var(--space-3)' }}>
              <button
                type="button"
                className="btn"
                disabled={status === 'loading' || (TURNSTILE_ENABLED && !tsToken) || !accepted}
                onClick={() => submit()}
              >
                {status === 'loading' ? t('checkout.preparing') : t('checkout.confirmPay')}
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                disabled={status === 'loading'}
                onClick={() => setStage('form')}
              >
                {t('checkout.editOrder')}
              </button>
            </div>
            <p className="text-muted" style={{ fontSize: 'var(--fs-xs)' }}>
              {t('checkout.secureNote')}
            </p>
          </div>
        )}

        <aside className="stack" style={{ background: 'var(--color-white)', padding: 'var(--space-5)', borderRadius: 'var(--radius-md)', border: 'var(--border-hairline)' }}>
          <p className="eyebrow">{t('checkout.summary')}</p>
          {items.map((i) =>
            isPackItem(i) ? (
              <div key={itemKey(i)} style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
                <span>
                  <Link to={lp(`${PACK_PATH}/${i.pack.slug}`)} className="link-underline">
                    {packName(i.pack, locale)}
                  </Link>{' '}
                  <span className="text-muted">× {i.quantity}</span>
                  <span className="cart-line__includes">{includes(i.pack)}</span>
                </span>
                <span className="price">{formatMoney(i.pack.priceCents * i.quantity, currency)}</span>
              </div>
            ) : (
            <div key={itemKey(i)} style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
              <span>
                {i.product.name}
                {i.product.size ? <span className="text-muted"> · {t('cart.size')} {i.product.size}</span> : null}{' '}
                <span className="text-muted">× {i.quantity}</span>
              </span>
              <span className="price">
                {i.product.priceCents > 0 ? formatMoney(i.product.priceCents * i.quantity, currency) : '—'}
              </span>
            </div>
            ),
          )}
          <hr className="divider" style={{ margin: 'var(--space-3) 0' }} />

          {/* Subtotal */}
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{t('cart.subtotal')}</span>
            <span className="price">{formatMoney(quote?.totals.subtotalCents ?? subtotalCents, currency)}</span>
          </div>

          {/* VAT included (informational — prices are VAT-inclusive) */}
          <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--color-text-muted)', fontSize: 'var(--fs-sm)' }}>
            <span>{t('checkout.vat')}</span>
            <span>{quote ? formatMoney(quote.totals.taxCents, currency) : t('checkout.calculating')}</span>
          </div>

          {/* Shipping */}
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{t('checkout.shippingLabel')}</span>
            <span className="price">
              {!hasAddress
                ? t('checkout.shippingPending')
                : quoteLoading || !quote
                  ? t('checkout.calculating')
                  : quote.totals.shippingCents === 0
                    ? t('checkout.free')
                    : formatMoney(quote.totals.shippingCents - (quote.totals.shippingSurchargeCents ?? 0), currency)}
            </span>
          </div>

          {deliveryEstimate ? (
            <p className="text-muted delivery-estimate" style={{ fontSize: 'var(--fs-sm)', margin: 0 }}>
              {deliveryEstimate}
            </p>
          ) : null}

          {/* Temporary fuel surcharge, broken out of the shipping line */}
          {hasAddress && !quoteLoading && quote?.totals.shippingSurchargeCents ? (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{t('checkout.fuelSurcharge')}</span>
              <span className="price">{formatMoney(quote.totals.shippingSurchargeCents, currency)}</span>
            </div>
          ) : null}

          <hr className="divider" style={{ margin: 'var(--space-3) 0' }} />

          {/* Total */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <strong>{t('checkout.total')}</strong>
            <strong className="price" style={{ fontSize: 'var(--fs-md)' }}>
              {!hasAddress || !quote
                ? formatMoney(quote?.totals.subtotalCents ?? subtotalCents, currency) + '…'
                : formatMoney(quote.totals.totalCents, currency)}
            </strong>
          </div>

          <p className="text-muted" style={{ fontSize: 'var(--fs-xs)' }}>
            {t('checkout.shippingNote2')}
          </p>
          {!isEU(form.shippingCountry) ? (
            <p className="text-muted" style={{ fontSize: 'var(--fs-xs)' }}>
              {t('checkout.customsNote')}
            </p>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
